import brightcovePlayerLoader from '@brightcove/player-loader';
import playlistUi from 'videojs-playlist-ui';
import 'core-js/modules/es.promise';
import '@/Scripts/polyfills/nodelist.foreach.js';

const campaignPlayers = new WeakMap();
const reducedMotionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };
const modalTransitionDuration = 360;
let activeLightbox = null;

function setCampaignPlayerInteractive(video, interactive) {
    const playerSurface = video.querySelector('.video-frame, .video-container');

    if (playerSurface) {
        playerSurface.inert = !interactive;
    }
}

function setYouTubePlayback(video, command) {
    const iframe = video.querySelector('.video-container iframe');

    if (!iframe || !iframe.contentWindow) {
        return;
    }

    iframe.contentWindow.postMessage(JSON.stringify({
        event: 'command',
        func: command,
        args: []
    }), '*');
}

function setBackgroundInert(modal, inert) {
    Array.from(document.body.children).forEach(element => {
        if (element === modal) {
            return;
        }

        if (inert) {
            element.dataset.videoLightboxWasInert = element.inert ? 'true' : 'false';
            element.inert = true;
        } else {
            element.inert = element.dataset.videoLightboxWasInert === 'true';
            delete element.dataset.videoLightboxWasInert;
        }
    });
}

function trapLightboxFocus(event, modal) {
    if (event.key !== 'Tab') {
        return;
    }

    const focusableElements = Array.from(modal.querySelectorAll(
        'a[href], button, input, select, textarea, iframe, [tabindex]'
    )).filter(element => element.tabIndex >= 0
        && !element.matches(':disabled')
        && !element.closest('[inert], [hidden], [aria-hidden="true"]')
        && element.getClientRects().length > 0
        && window.getComputedStyle(element).visibility === 'visible');

    if (!focusableElements.length) {
        event.preventDefault();
        modal.querySelector('.videoLightbox-panel').focus();
        return;
    }

    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];

    const focusIsOutsideControls = !focusableElements.includes(document.activeElement);

    if (event.shiftKey && (document.activeElement === firstFocusableElement || focusIsOutsideControls)) {
        event.preventDefault();
        lastFocusableElement.focus();
    } else if (!event.shiftKey && (document.activeElement === lastFocusableElement || focusIsOutsideControls)) {
        event.preventDefault();
        firstFocusableElement.focus();
    }
}

function pauseCampaignVideo(video) {
    const playerPromise = campaignPlayers.get(video);

    if (playerPromise) {
        playerPromise.then(player => player.pause()).catch(() => {});
    }

    setYouTubePlayback(video, 'pauseVideo');
}

function playCampaignVideo(video) {
    const playerPromise = campaignPlayers.get(video);

    if (playerPromise) {
        playerPromise.then(player => {
            const playPromise = player.play();

            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(() => {});
            }
        }).catch(() => {});
    }

    setYouTubePlayback(video, 'playVideo');
}

function createVideoLightbox(closeLabel, title) {
    const modal = document.createElement('div');
    const backdrop = document.createElement('button');
    const panel = document.createElement('div');
    const content = document.createElement('div');
    const closeButton = document.createElement('button');

    modal.className = 'videoLightbox';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', title);

    backdrop.className = 'videoLightbox-backdrop';
    backdrop.type = 'button';
    backdrop.tabIndex = -1;
    backdrop.setAttribute('aria-label', closeLabel);

    panel.className = 'videoLightbox-panel';
    panel.tabIndex = -1;

    content.className = 'videoLightbox-content';

    closeButton.className = 'videoLightbox-close';
    closeButton.type = 'button';
    closeButton.setAttribute('aria-label', closeLabel);

    panel.appendChild(content);
    panel.appendChild(closeButton);
    modal.appendChild(backdrop);
    modal.appendChild(panel);
    document.body.appendChild(modal);

    return { modal, backdrop, panel, content, closeButton };
}

function createVideoPlaceholder(video) {
    const placeholder = video.cloneNode(true);

    placeholder.classList.remove('is-in-lightbox');
    placeholder.classList.add('video--lightboxPlaceholder');
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.inert = true;

    placeholder.querySelectorAll('[id]').forEach(element => element.removeAttribute('id'));
    placeholder.querySelectorAll('[aria-controls], [aria-describedby], [aria-labelledby]').forEach(element => {
        element.removeAttribute('aria-controls');
        element.removeAttribute('aria-describedby');
        element.removeAttribute('aria-labelledby');
    });
    placeholder.querySelectorAll('.video-js').forEach(player => {
        player.classList.remove('vjs-has-started', 'vjs-playing', 'vjs-ended');
        player.classList.add('vjs-paused');
    });

    return placeholder;
}

function closeVideoLightbox() {
    if (!activeLightbox || activeLightbox.isClosing) {
        return;
    }

    activeLightbox.isClosing = true;

    const { modal, video, placeholder, launcher } = activeLightbox;
    const closeDelay = reducedMotionQuery.matches ? 0 : modalTransitionDuration;

    pauseCampaignVideo(video);
    setCampaignPlayerInteractive(video, false);
    modal.classList.remove('is-open');

    window.setTimeout(() => {
        if (placeholder.parentNode) {
            placeholder.parentNode.replaceChild(video, placeholder);
        }

        video.classList.remove('is-in-lightbox');
        modal.remove();
        document.body.classList.remove('videoLightbox-isOpen');
        setBackgroundInert(modal, false);
        launcher.setAttribute('aria-expanded', 'false');
        launcher.focus();
        activeLightbox = null;
    }, closeDelay);
}

function openVideoLightbox(video, launcher) {
    if (activeLightbox) {
        return;
    }

    const placeholder = createVideoPlaceholder(video);
    const title = video.dataset.videoTitle || launcher.getAttribute('aria-label');
    const closeLabel = launcher.dataset.videoCloseLabel || 'Lukk video';
    const lightbox = createVideoLightbox(closeLabel, title);

    video.parentNode.insertBefore(placeholder, video);
    video.classList.add('is-in-lightbox');
    setCampaignPlayerInteractive(video, true);
    lightbox.content.appendChild(video);
    lightbox.modal.hidden = false;
    launcher.setAttribute('aria-expanded', 'true');
    document.body.classList.add('videoLightbox-isOpen');
    setBackgroundInert(lightbox.modal, true);

    activeLightbox = { ...lightbox, video, placeholder, launcher };

    lightbox.backdrop.addEventListener('click', closeVideoLightbox);
    lightbox.closeButton.addEventListener('click', closeVideoLightbox);
    lightbox.modal.addEventListener('click', event => {
        if (!event.target.closest('.video-frame, .video-container, .videoLightbox-close')) {
            closeVideoLightbox();
        }
    });
    lightbox.modal.addEventListener('keydown', event => {
        if (event.key === 'Escape' || event.key === 'Esc') {
            closeVideoLightbox();
            return;
        }

        trapLightboxFocus(event, lightbox.modal);
    });

    window.requestAnimationFrame(() => {
        lightbox.modal.classList.add('is-open');
        lightbox.closeButton.focus();
        playCampaignVideo(video);
    });
}

function initCampaignVideo(video) {
    const launcher = video.querySelector('[data-video-lightbox-open]');

    if (!launcher || launcher.dataset.videoLightboxInitialized === 'true') {
        return;
    }

    launcher.dataset.videoLightboxInitialized = 'true';
    launcher.setAttribute('aria-expanded', 'false');
    setCampaignPlayerInteractive(video, false);
    launcher.addEventListener('click', () => openVideoLightbox(video, launcher));
}

const videos = document.querySelectorAll('.video-frame');

if (videos.length) {
    videos.forEach((video) => {
        const videoRoot = video.closest('.video');
        const accessibleTitle = video.dataset['videoTitle'];
        const isCampaignStyle = video.dataset['videoStyle'] === 'campaign';
        const playlistId = video.dataset['playlistId'];

        const playerPromise = brightcovePlayerLoader({
            refNode: video,
            accountId: video.dataset['accountId'],
            videoId: video.dataset['videoId'],
            playlistId,
            embedOptions: {
                playlist: Boolean(playlistId)
            },
            options: isCampaignStyle ? {
                inactivityTimeout: 900,
                controlBar: {
                    volumePanel: {
                        inline: false
                    }
                }
            } : undefined,
            Promise: Promise
        }).then((success) => {
            const iframe = video.querySelector('iframe');

            if (iframe && accessibleTitle) {
                iframe.title = accessibleTitle;
            }

            if (playlistId && typeof success.ref.playlist === 'function') {
                playlistUi.apply(success.ref);
            }

            return success.ref;
        });

        if (isCampaignStyle && videoRoot) {
            campaignPlayers.set(videoRoot, playerPromise);
        }
    });
}

document.querySelectorAll('.video--campaign').forEach(initCampaignVideo);
